import { clamp } from "@/utils/math"
import { Adjustments } from "@/core/types"

const MonotonicCubicSpline = (function () {
	function MonotonicCubicSpline(x: number[], y: number[]) {
		let i: number, j: number, len, len1, o, p, q, r, ref, ref1, ref2, ref3, to_fix: number[], v
		const n = x.length
		const delta: number[] = []
		const m: number[] = []
		const alpha: number[] = []
		const beta: number[] = []
		const dist: number[] = []
		const tau: number[] = []
		for (i = j = 0, ref = n - 1; 0 <= ref ? j < ref : j > ref; i = 0 <= ref ? ++j : --j) {
			delta[i] = (<number>y[i + 1] - <number>y[i]) / (<number>x[i + 1] - <number>x[i])
			if (i > 0) {
				m[i] = (<number>delta[i - 1] + <number>delta[i]) / 2
			}
		}
		m[0] = delta[0] as number
		m[n - 1] = delta[n - 2] as number
		to_fix = []
		for (i = o = 0, ref1 = n - 1; 0 <= ref1 ? o < ref1 : o > ref1; i = 0 <= ref1 ? ++o : --o) {
			if (delta[i] === 0) {
				to_fix.push(i)
			}
		}
		for (p = 0, len = to_fix.length; p < len; p++) {
			i = to_fix[p] as number
			m[i] = m[i + 1] = 0
		}
		for (i = q = 0, ref2 = n - 1; 0 <= ref2 ? q < ref2 : q > ref2; i = 0 <= ref2 ? ++q : --q) {
			const deltaI = delta[i] as number
			alpha[i] = <number>m[i] / deltaI
			beta[i] = <number>m[i + 1] / deltaI
			dist[i] = Math.pow(<number>alpha[i], 2) + Math.pow(<number>beta[i], 2)
			tau[i] = 3 / Math.sqrt(<number>dist[i])
		}
		to_fix = []
		for (i = r = 0, ref3 = n - 1; 0 <= ref3 ? r < ref3 : r > ref3; i = 0 <= ref3 ? ++r : --r) {
			if (<number>dist[i] > 9) {
				to_fix.push(i)
			}
		}
		for (v = 0, len1 = to_fix.length; v < len1; v++) {
			i = to_fix[v] as number
			m[i] = <number>tau[i] * <number>alpha[i] * <number>delta[i]
			m[i + 1] = <number>tau[i] * <number>beta[i] * <number>delta[i]
		}

		// @ts-expect-error `this` implicitly has type `any`
		this.x = x.slice(0, n)
		// @ts-expect-error `this` implicitly has type `any`
		this.y = y.slice(0, n)
		// @ts-expect-error `this` implicitly has type `any`
		this.m = m
	}

	MonotonicCubicSpline.prototype.interpolate = function (x) {
		let i, j, ref
		for (
			i = j = ref = this.x.length - 2;
			ref <= 0 ? j <= 0 : j >= 0;
			i = ref <= 0 ? ++j : --j
		) {
			if (this.x[i] <= x) {
				break
			}
		}
		const h = this.x[i + 1] - this.x[i]
		const t = (x - this.x[i]) / h
		const t2 = Math.pow(t, 2)
		const t3 = Math.pow(t, 3)
		const h00 = 2 * t3 - 3 * t2 + 1
		const h10 = t3 - 2 * t2 + t
		const h01 = -2 * t3 + 3 * t2
		const h11 = t3 - t2
		const y =
			h00 * this.y[i] + h10 * h * this.m[i] + h01 * this.y[i + 1] + h11 * h * this.m[i + 1]
		return y
	}

	return MonotonicCubicSpline
})()

const hslKeys = [
	"hueRed",
	"saturationRed",
	"luminanceRed",
	"hueOrange",
	"saturationOrange",
	"luminanceOrange",
	"hueYellow",
	"saturationYellow",
	"luminanceYellow",
	"hueGreen",
	"saturationGreen",
	"luminanceGreen",
	"hueAqua",
	"saturationAqua",
	"luminanceAqua",
	"hueBlue",
	"saturationBlue",
	"luminanceBlue",
	"huePurple",
	"saturationPurple",
	"luminancePurple",
	"hueMagenta",
	"saturationMagenta",
	"luminanceMagenta",
] as const

type HSLKeys = (typeof hslKeys)[number]

const pointsMap = {
	red: [0, 8],
	orange: [1],
	yellow: [2],
	green: [3],
	aqua: [4],
	blue: [5],
	purple: [6],
	magenta: [7],
} as const

const offsetsMap = {
	hue: 0,
	saturation: 1,
	luminance: 2,
}

const points = {
	hue: [127.5, 127.5, 127.5, 127.5, 127.5, 127.5, 127.5, 127.5, 127.5],
	saturation: [127.5, 127.5, 127.5, 127.5, 127.5, 127.5, 127.5, 127.5, 127.5],
	luminance: [127.5, 127.5, 127.5, 127.5, 127.5, 127.5, 127.5, 127.5, 127.5],
	degrees: [0, 30, 55, 95, 160, 230, 270, 330, 360],
}

export const hueShiftScaleMap = {
	red: [1.4, 1.25],
	orange: [1, 1],
	yellow: [1, 1.25],
	green: [1.25, 2.0],
	aqua: [2, 2],
	blue: [2.5, 1.5],
	purple: [1.4, 1.3],
	magenta: [1.6, 1.15],
} as const

const adjustmentsMap: Record<HSLKeys, [keyof typeof offsetsMap, keyof typeof pointsMap]> = {
	hueRed: ["hue", "red"],
	hueYellow: ["hue", "yellow"],
	hueGreen: ["hue", "green"],
	hueAqua: ["hue", "aqua"],
	hueBlue: ["hue", "blue"],
	huePurple: ["hue", "purple"],
	hueMagenta: ["hue", "magenta"],
	hueOrange: ["hue", "orange"],

	saturationRed: ["saturation", "red"],
	saturationOrange: ["saturation", "orange"],
	saturationYellow: ["saturation", "yellow"],
	saturationGreen: ["saturation", "green"],
	saturationAqua: ["saturation", "aqua"],
	saturationBlue: ["saturation", "blue"],
	saturationPurple: ["saturation", "purple"],
	saturationMagenta: ["saturation", "magenta"],

	luminanceRed: ["luminance", "red"],
	luminanceOrange: ["luminance", "orange"],
	luminanceYellow: ["luminance", "yellow"],
	luminanceGreen: ["luminance", "green"],
	luminanceAqua: ["luminance", "aqua"],
	luminanceBlue: ["luminance", "blue"],
	luminancePurple: ["luminance", "purple"],
	luminanceMagenta: ["luminance", "magenta"],
}

const textureData = new Float32Array(360 * 3)

//@din TODO: rewrite naive and inefficient implementation
export function getHSLData(values: Adjustments): Float32Array {
	for (const key of hslKeys) {
		const [channel, color] = adjustmentsMap[key]

		const pointMap = pointsMap[color]
		const hueShifts = channel === "hue" ? hueShiftScaleMap[color] : [1, 1]

		const value = values[key] * 0.5
		const hueShiftScale = value >= 0 ? hueShifts[1] : hueShifts[0]

		for (const element of pointMap) {
			points[channel][element] = ((value * hueShiftScale) / 2 + 0.5) * 255
		}
	}

	for (const channel of ["hue", "saturation", "luminance"]) {
		const pointsSpline: { interpolate: (i: number) => number } = new MonotonicCubicSpline(
			points.degrees,
			points[channel],
		)
		const offset = offsetsMap[channel]

		let point = 0

		for (let i = 0; i < 360; i++) {
			textureData[i * 3 + offset] = point =
				(clamp(pointsSpline.interpolate(i), 0, 255) >> 0 || point) / 255
		}
	}

	return textureData
}
